import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Bot } from 'lucide-react';
import { apiClient } from '../lib/api';
import { Card } from './ui/Card';
import { CountryFlag } from './ui/CountryFlag';
import { usePollMultiplier } from '../hooks/usePollMultiplier';

// Baris mentah dari DO stream — subset dari LiveClick (Clicks.tsx) saja
// yang dipakai feed ringkas ini.
interface PulseClick {
  timestamp: string;
  click_id: string;
  campaign_slug: string;
  country: string;
  device: string;
  is_bot: number;
}

function hhmmss(ts: string | number): string {
  const ms = Number(ts);
  const d = Number.isFinite(ms) && ts != null ? new Date(ms) : new Date(ts);
  return isNaN(d.getTime())
    ? '--:--:--'
    : d.toLocaleTimeString('en-GB', { hour12: false });
}

/**
 * Feed klik realtime (DO stream, latensi sub-2s) dalam kartu kompak untuk
 * Overviews. Delta-fetch sama seperti halaman /clicks: kirim `since` agar
 * payload per poll kecil, akumulasi di ref supaya buffer tetap penuh.
 */
export function RealtimePulseCard({ maxRows = 8 }: { maxRows?: number }) {
  const pollMult = usePollMultiplier();
  const bufferRef = useRef<PulseClick[]>([]);
  const sinceRef = useRef<number>(0);

  const pulse = useQuery({
    queryKey: ['realtime-pulse'],
    queryFn: async () => {
      const delta = await apiClient.get<PulseClick[]>('/api/v1/clicks/realtime', {
        limit: 30,
        since: sinceRef.current || undefined,
      });
      let newest = sinceRef.current;
      for (const r of delta) {
        const t = Number(r.timestamp);
        if (Number.isFinite(t) && t > newest) newest = t;
      }
      if (newest > 0) sinceRef.current = newest;
      bufferRef.current = [...delta, ...bufferRef.current].slice(0, 50);
      return bufferRef.current;
    },
    refetchInterval: 5_000 * pollMult,
    refetchIntervalInBackground: false,
  });

  const rows = (pulse.data ?? []).slice(0, maxRows);

  return (
    <Card className="p-0">
      <div className="px-5 py-4 border-b border-border-subtle flex items-center justify-between gap-2">
        <h3 className="font-display text-sm uppercase tracking-wider text-neon-cyan flex items-center gap-2">
          <Activity size={14} /> Realtime Clicks
        </h3>
        <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-text-tertiary">
          <span className="w-1.5 h-1.5 rounded-full bg-neon-green animate-pulse" />
          live · sub-2s
        </span>
      </div>
      <div className="divide-y divide-border-subtle/60">
        {rows.map((r) => (
          <div key={r.click_id} className="px-5 py-1.5 flex items-center gap-3 font-mono text-[11px]">
            <span className="text-text-tertiary shrink-0">{hhmmss(r.timestamp)}</span>
            <span className="text-neon-cyan truncate flex-1">{r.campaign_slug}</span>
            <CountryFlag iso2={r.country} showCode size={16} />
            {Number(r.is_bot) === 1 ? (
              <Bot size={12} className="text-neon-orange shrink-0" />
            ) : (
              <span className="w-3" />
            )}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="px-5 py-8 text-center font-mono text-xs text-text-tertiary">
            waiting for clicks…
          </div>
        )}
      </div>
    </Card>
  );
}
